import type { DesignBody, FontLookup, InkColorLookup } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useFontsByIdsQuery } from '@/entities/font';
import { useInkColorsByIdsQuery } from '@/entities/ink-color';
import { extractQuoteDesignContents } from '@/entities/quote';
import { ContentCard, cn, detailListStyles as styles, tableStyles } from '@/shared/ui';
import { QuoteFontLabel } from './quote-font-label';
import { QuoteInkLabel } from './quote-ink-label';

export interface QuoteDesignContentsProps {
  /** 견적 요청 때 고정한 디자인 사본(design_body). */
  readonly designBody: DesignBody;
}

/**
 * 텍스트·폰트·잉크 목록(견적 설계 §6-2 "복사본에서 읽는다") — 뷰어 없이도 제작 전에 문구와 쓰인 폰트·잉크를 확인한다.
 * 사본에는 id 만 있어 이름은 지금 마스터에서 찾는다(결정 D12·D13) — 지운 폰트·잉크도 '(삭제됨)'을 붙여 보여 준다.
 * 불러오는 중·실패·못 찾은 id 는 id 를 그대로 보여 준다.
 */
export const QuoteDesignContents = ({ designBody }: QuoteDesignContentsProps): ReactElement => {
  const { texts, fontIds, inkColorIds } = extractQuoteDesignContents(designBody);
  const fontsQuery = useFontsByIdsQuery(fontIds);
  const inkColorsQuery = useInkColorsByIdsQuery(inkColorIds);
  const fontsById = new Map(
    (fontsQuery.data ?? []).map((font): [string, FontLookup] => [font.id, font]),
  );
  const inkColorsById = new Map(
    (inkColorsQuery.data ?? []).map((inkColor): [string, InkColorLookup] => [
      inkColor.id,
      inkColor,
    ]),
  );

  return (
    <ContentCard title="텍스트·폰트·잉크">
      {texts.length === 0 ? (
        <p className="text-sm text-muted-foreground">텍스트를 쓰지 않은 디자인입니다.</p>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="overflow-x-auto rounded-xl border border-foreground/[0.06]">
            <table aria-label="디자인 텍스트" className={tableStyles.table}>
              <thead className={tableStyles.head}>
                <tr>
                  <th className={tableStyles.th}>문구</th>
                  <th className={tableStyles.th}>폰트</th>
                  <th className={tableStyles.th}>잉크</th>
                </tr>
              </thead>
              <tbody>
                {texts.map((text) => (
                  <tr key={text.nodeId} className={tableStyles.row}>
                    <td
                      className={cn(tableStyles.td, 'align-top whitespace-pre-wrap wrap-anywhere')}
                    >
                      {text.text}
                    </td>
                    <td className={cn(tableStyles.td, 'align-top')}>
                      <QuoteFontLabel fontId={text.fontId} font={fontsById.get(text.fontId)} />
                    </td>
                    <td className={cn(tableStyles.td, 'align-top')}>
                      <QuoteInkLabel
                        inkColorId={text.inkColorId}
                        inkColor={inkColorsById.get(text.inkColorId)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <h3 className={styles.subheading}>쓰인 폰트</h3>
              <ul aria-label="쓰인 폰트" className="mt-2 flex flex-col gap-1 text-sm">
                {fontIds.map((fontId) => (
                  <li key={fontId}>
                    <QuoteFontLabel fontId={fontId} font={fontsById.get(fontId)} />
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className={styles.subheading}>쓰인 잉크</h3>
              <ul aria-label="쓰인 잉크" className="mt-2 flex flex-col gap-1 text-sm">
                {inkColorIds.map((inkColorId) => (
                  <li key={inkColorId}>
                    <QuoteInkLabel
                      inkColorId={inkColorId}
                      inkColor={inkColorsById.get(inkColorId)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </ContentCard>
  );
};
